import { z } from 'zod';

/**
 * This module is a deliberate leaf: it must never import from
 * `prepackaged-label-regulatory.ts` or `prepackaged-vertical-origin.ts`, both
 * of which import FROM here. Those two modules imported each other for a few
 * hours on 2026-08-21 (regulatory -> vertical-origin for
 * `PREPACKAGED_VERTICAL_ORIGIN_REGIMES`, vertical-origin -> regulatory for
 * the country-code type/guard) and broke at runtime with a temporal-dead-zone
 * `ReferenceError` depending on which module's bundler entry point happened
 * to import the other first -- `tsc` and every `shared` unit test stayed
 * green throughout, because neither catches a cycle. See
 * `prepackaged-import-cycle.guardian.test.ts`, which does.
 */

/**
 * Current ISO 3166-1 alpha-2 country and territory codes.
 *
 * Keep this complete catalogue in the shared contract so the browser cannot
 * silently discard a valid stored market and the API validates the same set
 * of codes. The list follows the IANA tzdb `iso3166.tab` catalogue (249
 * active ISO 3166-1 entries), rather than a hand-picked sales-market subset.
 */
export const PREPACKAGED_MARKET_COUNTRY_CODES = [
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UM',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW',
] as const;

/** Every reader of a vertical-origin country field narrows to this instead of `string` (D / task 4). */
export type PrepackagedMarketCountryCode = (typeof PREPACKAGED_MARKET_COUNTRY_CODES)[number];

const prepackagedMarketCountryCodeSet = new Set<string>(PREPACKAGED_MARKET_COUNTRY_CODES);

/**
 * Runtime membership check reused wherever a stored value must be a genuine
 * ISO 3166-1 alpha-2 market code and not, e.g., a vertical-origin aggregate
 * pseudo-code (`UE`, `NON_EU`, ...) or free text a form field once allowed.
 */
export function isPrepackagedMarketCountryCode(
  value: unknown,
): value is PrepackagedMarketCountryCode {
  return typeof value === 'string' && prepackagedMarketCountryCodeSet.has(value);
}

export const PrepackagedMarketCountryCodeSchema = z
  .string()
  .regex(/^[A-Z]{2}$/, 'expected ISO-3166 alpha-2 code')
  .refine((code) => prepackagedMarketCountryCodeSet.has(code), {
    message: 'expected a current ISO-3166 alpha-2 code',
  });

/**
 * The single place that turns a stored ISO 3166-1 alpha-2 code into the
 * Italian country name every vertical-origin decree prints (Reg. (UE)
 * 1169/2011 art. 9 §1 lett. i) and each decree in
 * `prepackaged-vertical-origin.ts`). Every composer that used to interpolate
 * a free-text country field now calls this instead, so the printed wording
 * and the stored code can never drift apart — the trap this module has
 * fallen into before (see `docs/plans/prepackaged-D-origine.md` task 4).
 *
 * Backed by `Intl.DisplayNames`, the CLDR region-name table the runtime
 * already ships, rather than a hand-transcribed second 249-row table next to
 * `PREPACKAGED_MARKET_COUNTRY_CODES` that would inevitably drift from it.
 */
const italianRegionNames = new Intl.DisplayNames(['it'], { type: 'region' });

/**
 * Falls back to the raw code only if the runtime's CLDR data has no entry
 * for it — every code in `PREPACKAGED_MARKET_COUNTRY_CODES` resolves, so this
 * is a defensive branch, not an expected path (covered by
 * `prepackaged-country-names.test.ts`, which asserts all 249 do).
 */
export function prepackagedCountryNameIt(code: PrepackagedMarketCountryCode | string): string {
  try {
    return italianRegionNames.of(code) ?? code;
  } catch {
    return code;
  }
}
